import { Pmp_Configs, Pmps_Metadata, TokenDetailsFragment, Pmp_Param_Types_Enum } from '@/gql/generated/graphql'
import { AddressDisplay } from '@components/address-display'
import { LoadingIndicator } from '@components/loading-indicator'
import { Toggle } from '@radix-ui/react-toggle'
import { useTokenActivity } from '@reservoir0x/reservoir-kit-ui'
import {
  ArrowBendDownRightIcon,
  CoinIcon,
  PlantIcon,
  TagIcon,
  PencilSimpleIcon,
  ArrowUpRightIcon
} from 'design-configuration/components'
import { AnimatePresence, motion } from 'motion/react'
import { useCallback, useState } from 'react'
import { Hex } from 'viem'
import { formatDate, formatConfiguredPMPValue } from '../utils'
import { getEtherscanTransactionURL } from '@utils/routes'
import { TokenInfoListItem } from './token-info-list-item'
import { userProfileRoute } from '@utils/routes'
import { ScrollSentinel } from '@components/scroll-sentinel'
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger, BodyText } from 'design-configuration/components'

const PROVENANCE_EVENT_TYPES = ['mint', 'ask', 'sale', 'transfer', 'customization'] as const
type ProvenanceEvent = (typeof PROVENANCE_EVENT_TYPES)[number]

export function Provenance({ token }: { token: TokenDetailsFragment }): React.ReactElement {
  const [allowedEventTypes, setAllowedEventTypes] = useState([...PROVENANCE_EVENT_TYPES])

  const allPmps = [...(token.pmps || [])].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )
  const projectPmpConfig = token.project.pmp_project_configs?.[0]?.pmp_configs

  const showCustomizations = allowedEventTypes.includes('customization')
  const {
    data: tokenEvents,
    isFetchingInitialData,
    hasNextPage,
    fetchNextPage,
    isFetchingPage
  } = useTokenActivity(token.id.replace('-', ':'), {
    types: allowedEventTypes.filter((type) => type !== 'customization')
  })

  const handleScrollSentinelInViewChange = useCallback(
    (inView: boolean) => {
      if (inView && hasNextPage && !isFetchingPage && !isFetchingInitialData) {
        fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingPage, isFetchingInitialData]
  )

  const formatPmpValues = useCallback((pmp: Pmps_Metadata, pmpConfig: Pmp_Configs): string | null | boolean => {
    const formattedValue = formatConfiguredPMPValue(pmp, pmpConfig)
    if (pmpConfig.param_type === Pmp_Param_Types_Enum.Timestamp && typeof formattedValue === 'string') {
      return formatDate(new Date(Number(formattedValue) * 1000).toISOString())
    }
    return formattedValue
  }, [])

  const EventTimeLink = ({ createdAt, txHash }: { createdAt: string; txHash: string | undefined }) => {
    if (!txHash) {
      return formatDate(createdAt)
    }

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      e.preventDefault()
      window.open(getEtherscanTransactionURL(txHash), '_blank', 'noopener,noreferrer')
    }

    return (
      <button
        type="button"
        onClick={handleClick}
        className="gap-2xs flex cursor-pointer items-center border-0 bg-transparent p-0 uppercase text-inherit transition-opacity hover:opacity-70"
        title={`View transaction on Etherscan: ${txHash}`}
      >
        {formatDate(createdAt)}
        <ArrowUpRightIcon className="fill-on-surface-variant h-[12px] w-[12px]" />
      </button>
    )
  }

  return (
    <div className="p-sm">
      <div className="gap-xs mb-sm flex">
        {PROVENANCE_EVENT_TYPES.map((eventType: ProvenanceEvent) => (
          <Toggle
            className="rounded-xs py-2xs px-xs border-outline-variant data-[state=on]:bg-surface data-[state=on]:border-outline text-body flex items-center justify-center border"
            key={eventType}
            defaultPressed={allowedEventTypes.includes(eventType)}
            pressed={allowedEventTypes.includes(eventType)}
            onPressedChange={(pressed) => {
              if (pressed) {
                setAllowedEventTypes([...allowedEventTypes, eventType])
              } else {
                setAllowedEventTypes(allowedEventTypes.filter((event) => event !== eventType))
              }
            }}
          >
            {eventType.slice(0, 1).toUpperCase() + eventType.slice(1)}
          </Toggle>
        ))}
      </div>
      <div className="relative min-h-20">
        <AnimatePresence>
          {isFetchingInitialData ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="p-md absolute inset-0 flex justify-center"
            >
              <LoadingIndicator className="w-lg h-lg fill-outline-variant" />
            </motion.div>
          ) : (
            <motion.div
              className="gap-xs flex flex-col"
              key="token-events"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <AnimatePresence>
                {showCustomizations &&
                  allPmps.map((pmp, index) => {
                    // find the previous pmp update if it exists
                    const previousPmp = allPmps.slice(index + 1).find((p) => p.key === pmp.key)
                    const config = projectPmpConfig?.find((c) => c.key === pmp.key)

                    const currentValue =
                      pmp.configured_value ||
                      pmp.artist_configured_value_string ||
                      pmp.non_artist_configured_value_string
                    const previousValue = previousPmp
                      ? previousPmp.configured_value ||
                        previousPmp.artist_configured_value_string ||
                        previousPmp.non_artist_configured_value_string
                      : null

                    // @dev edge case: pmp param type and project pmp configuration param type may be different
                    const currentValueFormatted =
                      currentValue !== null && currentValue !== undefined
                        ? formatPmpValues(pmp, config as Pmp_Configs)
                        : currentValue
                    const previousValueFormatted =
                      previousValue !== null && previousValue !== undefined
                        ? formatPmpValues(previousPmp as Pmps_Metadata, config as Pmp_Configs)
                        : previousValue

                    const formattedValueText = previousValueFormatted
                      ? `${previousValueFormatted} → ${currentValueFormatted}`
                      : currentValueFormatted

                    const bottomRightText = `${pmp.key}: ${formattedValueText}`

                    // truncate text if it's longer than 30 characters
                    const formattedBottomRightText =
                      bottomRightText.length > 30 ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="max-w-[200px] truncate">{bottomRightText}</div>
                          </TooltipTrigger>
                          <TooltipPortal>
                            <TooltipContent className="bg-surface p-md cursor-pointer border-0 shadow-[4px_4px_8px_-2px_rgba(0,0,0,0.1)]">
                              <div className="gap-2xs flex flex-col">
                                <BodyText className="text-sm font-medium">{pmp.key}</BodyText>
                                <BodyText className="text-sm">{formattedValueText}</BodyText>
                              </div>
                            </TooltipContent>
                          </TooltipPortal>
                        </Tooltip>
                      ) : (
                        bottomRightText
                      )

                    return (
                      <TokenInfoListItem
                        key={`${pmp.key}-${pmp.created_at}`}
                        topLeft={
                          <span className="gap-2xs flex items-center">
                            <PencilSimpleIcon className="fill-on-surface-variant" /> Customized By
                          </span>
                        }
                        topRight={formatDate(pmp.created_at)}
                        bottomLeft={<AddressDisplay address={pmp.configuring_address as Hex | undefined} />}
                        bottomRight={formattedBottomRightText}
                      />
                    )
                  })}

                {tokenEvents?.map((event) => {
                  if (event.type === PROVENANCE_EVENT_TYPES[0]) {
                    return (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        key={JSON.stringify(event)}
                      >
                        <TokenInfoListItem
                          topLeft={
                            <span className="gap-2xs flex items-center">
                              <PlantIcon className="fill-on-surface-variant" /> Mint By
                            </span>
                          }
                          topRight={
                            event.createdAt ? <EventTimeLink createdAt={event.createdAt} txHash={event.txHash} /> : null
                          }
                          bottomLeft={<AddressDisplay address={event.toAddress as Hex | undefined} />}
                          bottomRight={
                            event.price
                              ? `${event.price.amount?.decimal} ${event.price.currency?.symbol} ($${event.price.amount?.usd?.toFixed(2)})`
                              : null
                          }
                          href={userProfileRoute(event.toAddress as Hex)}
                        />
                      </motion.div>
                    )
                  }
                  // Ask
                  if (event.type === PROVENANCE_EVENT_TYPES[1]) {
                    return (
                      <TokenInfoListItem
                        key={JSON.stringify(event)}
                        topLeft={
                          <span className="gap-2xs flex items-center">
                            <TagIcon className="fill-on-surface-variant" /> Listed By
                          </span>
                        }
                        topRight={
                          event.createdAt ? <EventTimeLink createdAt={event.createdAt} txHash={event.txHash} /> : null
                        }
                        bottomLeft={<AddressDisplay address={event.fromAddress as Hex | undefined} />}
                        bottomRight={
                          event.price
                            ? `${event.price.amount?.decimal} ${event.price.currency?.symbol} ($${event.price.amount?.usd?.toFixed(2)})`
                            : null
                        }
                        href={userProfileRoute(event.fromAddress as Hex)}
                      />
                    )
                  }
                  // Sale
                  if (event.type === PROVENANCE_EVENT_TYPES[2]) {
                    return (
                      <TokenInfoListItem
                        key={JSON.stringify(event)}
                        topLeft={
                          <span className="gap-2xs flex items-center">
                            <CoinIcon className="fill-on-surface-variant" /> Sold To
                          </span>
                        }
                        topRight={
                          event.createdAt ? <EventTimeLink createdAt={event.createdAt} txHash={event.txHash} /> : null
                        }
                        bottomLeft={<AddressDisplay address={event.toAddress as Hex | undefined} />}
                        bottomRight={
                          event.price
                            ? `${event.price.amount?.decimal} ${event.price.currency?.symbol} ($${event.price.amount?.usd?.toFixed(2)})`
                            : null
                        }
                        href={userProfileRoute(event.toAddress as Hex)}
                      />
                    )
                  }

                  // Transfer
                  if (event.type === PROVENANCE_EVENT_TYPES[3]) {
                    return (
                      <TokenInfoListItem
                        key={JSON.stringify(event)}
                        topLeft={
                          <span className="gap-2xs flex items-center">
                            <ArrowBendDownRightIcon className="fill-on-surface-variant w-sm h-sm" /> Transferred To
                          </span>
                        }
                        topRight={
                          event.createdAt ? <EventTimeLink createdAt={event.createdAt} txHash={event.txHash} /> : null
                        }
                        bottomLeft={<AddressDisplay address={event.toAddress as Hex | undefined} />}
                        bottomRight={
                          <>
                            from <AddressDisplay address={event.fromAddress as Hex | undefined} />
                          </>
                        }
                        href={userProfileRoute(event.fromAddress as Hex)}
                      />
                    )
                  }

                  return null
                })}
                {isFetchingPage && (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="p-md flex w-full justify-center"
                  >
                    <LoadingIndicator className="w-lg h-lg fill-outline-variant" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
        <ScrollSentinel
          onInViewChange={handleScrollSentinelInViewChange}
          useInViewOptions={{
            margin: '50px',
            initial: true
          }}
        />
      </div>
    </div>
  )
}
